import type { GigsafePermission } from 'src/interfaces/gigsafeUser'

/**
 * `GET /v1/internal/partners/:partnerId/roles/permissions/all` returns the backend's `PermissionsAvailableForAssign`
 * constant verbatim — a `Record<string, GigsafePermissions[]>` of 22 group keys, NOT an array.
 * Anything treating it as an array renders an empty picker.
 *
 * Retool shows one flat, ungrouped list in object-key order, so the groups are flattened in
 * insertion order and de-duplicated (a few permissions appear under more than one group, e.g.
 * `roles_view` under both `Role` and `Users`-adjacent groupings).
 *
 * A non-object payload yields an empty list rather than throwing, so a backend shape change
 * degrades to an empty picker instead of a blank page.
 */
export const flattenAssignablePermissions = (payload: unknown): GigsafePermission[] => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return []
  }

  const groups = Object.values(payload as Record<string, unknown>)
  const seen = new Set<string>()
  const permissions: GigsafePermission[] = []

  groups.forEach((group) => {
    if (!Array.isArray(group)) {
      return
    }

    group.forEach((permission) => {
      if (typeof permission !== 'string' || seen.has(permission)) {
        return
      }

      seen.add(permission)
      permissions.push(permission)
    })
  })

  return permissions
}
